import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronDown, FlaskConical, ListTree, Pin } from 'lucide-react';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { RecordLink } from '../components/fields.js';
import {
  buttonClass,
  Card,
  cn,
  EmptyState,
  ErrorBanner,
  Modal,
  PageHeader,
  Spinner,
  Table,
  TD,
  TH,
} from '../components/ui.js';
import {
  formatCompactMoney,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatWholeMoney,
  LOCALE,
} from '../lib/format.js';
import { WelcomeCard } from '../help/welcome.js';
import { useAppMeta } from '../lib/meta.js';
import { useReadOnlyView } from '../lib/read-only.js';
import { useSlot } from '../lib/slots.js';
import { errorMessage, trpc, trpcClient, type RouterOutputs } from '../lib/trpc.js';
import { AskContext, AskForChange, type AskTarget } from './ask-for-change.js';
import {
  BarChart,
  cellText,
  NUMERIC,
  ReportCell,
  type ReportColumn,
  type ReportResult,
} from './reports.js';

/**
 * The owner dashboard (spec 16.4): one view at a time, drawn entirely from what the core
 * sends. A view is configuration, so a client's own views, figures and lists appear here
 * without anyone writing a screen for them.
 */

type View = RouterOutputs['dashboard']['view'];
type Figure = View['figures'][number];
type OwnerNumber = NonNullable<View['owner_number']>;
type Attention = View['attention'][number];
type Question = View['questions'][number];
type Panel = View['panels'][number];
type Source = View['sources'][number];
type Freshness = Figure['freshness'];
type FigureRecords = RouterOutputs['dashboard']['figure'];

/**
 * Reads what makes up a figure: the signed-in app asks as the person, a view link as its link.
 * The key keeps one link's answers apart from another's, and puts the app's with the rest of
 * the dashboard's, so they are read again when the dashboard is.
 */
export interface FigureLoader {
  key: (view: string, figure: string) => QueryKey;
  load: (view: string, figure: string) => Promise<FigureRecords>;
}

const FigureLoaderContext = createContext<FigureLoader | null>(null);

/** What makes up a figure, read as the person signed in. */
const signedIn: FigureLoader = {
  key: (view, figure) => trpc.dashboard.figure.queryKey({ view, key: figure }),
  load: (view, figure) => trpcClient.dashboard.figure.query({ view, key: figure }),
};

/** Panel tables show this many rows; the report behind them has the rest. */
const PANEL_ROWS = 10;

const SOURCE_STATE: Record<Source['state'], { dot: string; text: string }> = {
  fresh: { dot: 'bg-emerald-500', text: 'synced' },
  late: { dot: 'bg-amber-500', text: 'late, last synced' },
  failing: { dot: 'bg-red-500', text: 'failing, last synced' },
  never: { dot: 'bg-slate-300', text: 'not synced yet' },
};

const SEVERITY_DOT: Record<Attention['severity'], string> = {
  high: 'bg-loss',
  medium: 'bg-amber-500',
  low: 'bg-slate-300',
};

/** Whether a number as shown is below zero, drawn in red as a loss. It reads the text shown, so
 * an amount that rounds to $0 isn't red. */
function isNegative(shown: string): boolean {
  return /^\s*[-−(]/.test(shown);
}

/** `/dashboard` with no data yet, or with no views configured. */
export function DashboardEmpty() {
  const meta = useAppMeta();
  const canLoad = meta.capabilities.includes('manage_integrations');
  return (
    <>
      <PageHeader eyebrow="Owner dashboard" title="Nothing to show yet" />
      <WelcomeCard />
      <Card>
        <EmptyState title="The dashboard has no data yet">
          {meta.dashboard.views.length === 0
            ? meta.dashboard.has_data
              ? 'None of the dashboard’s views reads the kinds of records the business’s tools send.'
              : 'This deploy has no dashboard views configured.'
            : 'It fills in once the business’s own tools are connected or their exports imported.'}
          {canLoad && meta.dashboard.views.length > 0 && (
            <>
              {' '}
              To show how it works, load the sample business from{' '}
              <Link to="/settings" className="font-medium text-brand-strong hover:underline">
                Settings
              </Link>
              .
            </>
          )}
        </EmptyState>
      </Card>
    </>
  );
}

export function OwnerDashboard() {
  const meta = useAppMeta();
  const { view: key } = useParams({ strict: false }) as { view: string };
  const view = useQuery({
    ...trpc.dashboard.view.queryOptions({ key }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const summary = meta.dashboard.views.find((v) => v.key === key);
  const zone = meta.organization.timezone;

  return (
    <>
      <PageHeader
        eyebrow="Owner dashboard"
        title={view.data?.label ?? summary?.label ?? 'Dashboard'}
        subtitle={view.data?.description ?? summary?.description ?? undefined}
      />
      <WelcomeCard />
      <nav
        aria-label="Dashboard views"
        className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200"
      >
        {meta.dashboard.views.map((v) => (
          <Link
            key={v.key}
            to="/dashboard/$view"
            params={{ view: v.key }}
            className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium whitespace-nowrap text-slate-500 hover:text-slate-800"
            activeProps={{ className: 'border-brand! text-brand-strong!', 'aria-current': 'page' }}
          >
            {v.label}
          </Link>
        ))}
      </nav>
      {view.isLoading ? (
        <Spinner />
      ) : view.error || !view.data ? (
        <ErrorBanner title="Could not show this view">{errorMessage(view.error)}</ErrorBanner>
      ) : (
        <ViewBody
          view={view.data}
          zone={zone}
          company={meta.organization.name}
          canPin={meta.capabilities.includes('manage_config')}
          figures={signedIn}
          ask={
            meta.access.writable ? { view: view.data.key, ...meta.dashboard.change_requests } : null
          }
        />
      )}
    </>
  );
}

/**
 * One view's figures, attention list and panels, led on the first view by the owner's number
 * and on a view with one by the morning summary. Also what a read-only view link shows.
 */
export function ViewBody({
  view,
  zone,
  company,
  canPin: mayPin = false,
  sampleNote = true,
  figures = null,
  ask = null,
}: {
  view: View;
  zone: string;
  company: string;
  /** The viewer manages the configuration, so may pin the owner's number. */
  canPin?: boolean;
  /** Say here that the numbers are samples; false where the page around it already does. */
  sampleNote?: boolean;
  /** Reads what makes up a figure, so a figure opens its records; null for figures that don't. */
  figures?: FigureLoader | null;
  /** The signed-in app's view, so each card can ask for a change; null where none can. */
  ask?: AskTarget | null;
}) {
  const slotCards = useSlot('app.dashboard');
  const readOnly = useReadOnlyView();
  // A client's cards may reach for the signed-in app, so a read-only view leaves them out.
  const cards = readOnly ? [] : slotCards;
  const canPin = mayPin && !readOnly;
  const asking = readOnly ? null : ask;
  return (
    <FigureLoaderContext.Provider value={figures}>
      <AskContext.Provider value={asking}>
        <div className="space-y-5">
          {view.sample && sampleNote && (
            <div role="note" className="flex flex-wrap items-center gap-2 text-sm text-ink-3">
              <SamplePill />
              <p>
                Every number here is invented for a demonstration, dated to today.
                {view.sample.loaded_at && ` Loaded ${formatDateTime(view.sample.loaded_at, zone)}.`}
              </p>
            </div>
          )}
          {view.brief.length > 0 && <MorningSummary view={view} zone={zone} company={company} />}
          {view.owner_number && (
            <OwnerNumberCard number={view.owner_number} zone={zone} canPin={canPin} />
          )}
          {view.sources.length > 0 && <SourceLine sources={view.sources} zone={zone} />}
          {view.figures.length > 0 && (
            <div
              className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 [&>*]:min-w-0"
              data-tour="figures"
            >
              {view.figures.map((figure) => (
                <FigureCard
                  key={figure.key}
                  view={view.key}
                  figure={figure}
                  zone={zone}
                  pin={canPin ? { view: view.key, key: figure.key } : null}
                />
              ))}
            </div>
          )}
          {(view.attention.length > 0 || view.clear.length > 0) && (
            <NeedsYou attention={view.attention} clear={view.clear} zone={zone} />
          )}
          {view.questions.length > 0 && (
            // Keyed by view, so each view opens on its own first question.
            <Questions key={view.key} questions={view.questions} zone={zone} />
          )}
          {view.panels.length > 0 && (
            <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
              {view.panels.map((panel) => (
                <PanelCard key={panel.key} panel={panel} zone={zone} />
              ))}
            </div>
          )}
          {view.hidden.length > 0 && (
            <div className="rounded-xl border border-line bg-panel px-3 py-2 text-sm text-ink-2">
              <p className="font-medium text-slate-700">Some of this view is not shown to you</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {view.hidden.map((h) => (
                  <li key={h.key}>
                    {h.label}: {h.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cards.length > 0 && (
            <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
              {cards.map((card) => (
                <Card
                  look="dashboard"
                  key={card.id}
                  title={card.title}
                  className={cn(card.wide && 'lg:col-span-2')}
                  actions={<AskForChange kind="card" cardKey={card.id} label={card.title} />}
                >
                  <card.component />
                </Card>
              ))}
            </div>
          )}
          {asking && (
            <p className="text-sm text-ink-3">
              Something missing, or not how you want it?{' '}
              <AskForChange
                kind="view"
                cardKey={null}
                label={view.label}
                text="Ask for a change to this view"
                className="text-sm"
              />
            </p>
          )}
        </div>
      </AskContext.Provider>
    </FigureLoaderContext.Provider>
  );
}

function SourceLine({ sources, zone }: { sources: Source[]; zone: string }) {
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Where this comes from">
      {sources.map((s) => {
        const state = SOURCE_STATE[s.state];
        return (
          <li
            key={s.key}
            className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1 text-xs text-ink-3"
            title={s.last_error ?? undefined}
          >
            <span className={cn('size-2 rounded-full', state.dot)} aria-hidden />
            <span className="font-semibold text-ink-2">{s.label}</span>
            <span>
              {state.text}
              {s.last_success_at && ` ${formatDateTime(s.last_success_at, zone)}`}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** A headline number as it reads at a glance: whole dollars, a percent to one place. */
function figureText(figure: Pick<Figure, 'value' | 'type' | 'currency'>): string {
  const { value } = figure;
  if (value === null || value === '') return '—';
  switch (figure.type) {
    case 'money':
      return formatWholeMoney(value, figure.currency);
    case 'percent':
      return `${Number(value).toLocaleString(LOCALE, { maximumFractionDigits: 1 })}%`;
    case 'integer':
    case 'decimal':
      return Number(value).toLocaleString(LOCALE, { maximumFractionDigits: 1 });
    default:
      return String(value);
  }
}

/** "Good morning", "Good afternoon" or "Good evening", by the hour where the business is. */
function greeting(at: Date, zone: string): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: zone }).format(
      at,
    ),
  );
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
}

/** The business's day in running text: Tuesday, October 6. */
function dayText(at: Date, zone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: zone,
  }).format(at);
}

/** What an item has at stake, as a summary sentence says it: whole dollars. */
function briefAmount(item: Attention | undefined): string {
  if (!item || item.amount === null) return '';
  if (item.amount_type === 'money') return formatWholeMoney(item.amount, item.currency);
  return amountText(item);
}

/** A greeting and the most urgent items in a sentence each, so the view reads at a glance. */
function MorningSummary({ view, zone, company }: { view: View; zone: string; company: string }) {
  const at = new Date(view.generated_at);
  return (
    <section aria-labelledby="morning-summary" data-testid="morning-summary">
      <h2 id="morning-summary" className="text-xl font-bold tracking-tight text-balance text-ink">
        {greeting(at, zone)}. Here’s {company} on {dayText(at, zone)}.
      </h2>
      <ul className="mt-2 space-y-1 text-base text-ink-2">
        {view.brief.map((sentence) => {
          const item = view.attention.find((a) => a.key === sentence.key);
          return (
            <li key={sentence.key}>
              {sentence.text.replaceAll('{amount}', briefAmount(item))}
              {/* A source that is behind is said here too, not only on the item below. */}
              {item?.freshness.stale && (
                <span className="ml-1 text-sm text-amber-800">
                  {behindText(item.freshness, zone)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Pin a figure as the owner's number, or with null take it down. */
function usePin() {
  const queryClient = useQueryClient();
  return useMutation({
    // A new figure keeps what the owner said they wish they knew; its label is the figure's.
    mutationFn: async (figure: { view: string; key: string } | null) => {
      const asked = figure && (await trpcClient.dashboard.ownerNumber.query())?.asked;
      return trpcClient.dashboard.setOwnerNumber.mutate({
        number: figure && { figure, asked: asked ?? null },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.dashboard.pathKey() }),
  });
}

/** The owner's number, first and largest, with what they asked in their own words. */
function OwnerNumberCard({
  number,
  zone,
  canPin,
}: {
  number: OwnerNumber;
  zone: string;
  canPin: boolean;
}) {
  const unpin = usePin();
  const figure = number.figure;
  const [open, setOpen] = useState(false);
  const opens = useContext(FigureLoaderContext) !== null && number.view !== null;
  return (
    <section
      data-testid="owner-number"
      aria-labelledby="owner-number-label"
      data-tour="number"
      className="rounded-xl border-2 border-brand bg-surface p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-brand-strong uppercase">
          Your number
        </p>
        {canPin && (
          <button
            type="button"
            className={buttonClass('ghost', 'sm')}
            onClick={() => unpin.mutate(null)}
            disabled={unpin.isPending}
          >
            Unpin
          </button>
        )}
      </div>
      {number.asked && (
        <p className="mt-1 text-sm text-slate-600">
          What you wish you knew each morning:{' '}
          <q className="text-slate-800 italic">{number.asked}</q>
        </p>
      )}
      {figure ? (
        <>
          <p className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className={cn(
                'text-5xl font-bold tracking-tight text-ink tabular-nums',
                isNegative(figureText(figure)) && 'text-loss',
              )}
            >
              {figure.value === null && figure.empty ? '—' : figureText(figure)}
              {figure.unit && figure.value !== null && (
                <span className="ml-1 text-lg font-normal text-slate-500">{figure.unit}</span>
              )}
            </span>
            <span id="owner-number-label" className="text-lg font-semibold text-ink">
              {number.label}
            </span>
          </p>
          {figure.value === null && figure.empty && (
            <p className="mt-1 text-sm text-slate-600">{figure.empty}</p>
          )}
          {figure.value === null && <EarlierValue figure={figure} testId="owner-number-earlier" />}
          {figure.help && <p className="mt-2 text-sm text-slate-600">{figure.help}</p>}
          <div className="mt-1">
            <FromLine freshness={figure.freshness} zone={zone} />
          </div>
          <StaleNote freshness={figure.freshness} zone={zone} />
          {opens && figure.value !== null && (
            <WhatsInIt onClick={() => setOpen(true)} testId="owner-number-open" />
          )}
          {open && number.view !== null && (
            <FigureRecordsModal
              view={number.view}
              figure={figure.key}
              label={number.label}
              zone={zone}
              onClose={() => setOpen(false)}
            />
          )}
        </>
      ) : number.item ? (
        <OwnerNumberList item={number.item} label={number.label} zone={zone} />
      ) : (
        <>
          <p id="owner-number-label" className="mt-3 text-lg font-semibold text-slate-800">
            {number.label}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {number.hidden ??
              'This number isn’t on the dashboard yet. We’ll build it from your own numbers.'}
          </p>
        </>
      )}
      <AskForChange kind="owner_number" cardKey={null} label={number.label} className="mt-2" />
      {unpin.error && <p className="mt-2 text-sm text-red-700">{errorMessage(unpin.error)}</p>}
    </section>
  );
}

/**
 * The owner's number answered by a list, as "which jobs are in the red right now" is: how many
 * and what is at stake, and the records themselves.
 */
function OwnerNumberList({ item, label, zone }: { item: Attention; label: string; zone: string }) {
  const readOnly = useReadOnlyView();
  const amount = amountText(item);
  return (
    <>
      <p className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-5xl font-bold tracking-tight text-ink tabular-nums">
          {item.count}
          {item.more ? '+' : ''}
        </span>
        <span id="owner-number-label" className="text-lg font-semibold text-slate-800">
          {label}
        </span>
        {amount && item.count > 0 && (
          <span
            className={cn(
              'font-mono text-lg font-medium text-ink tabular-nums',
              isNegative(amount) && 'text-loss',
            )}
          >
            {amount}
          </span>
        )}
      </p>
      {item.help && <p className="mt-2 text-sm text-slate-600">{item.help}</p>}
      {item.count === 0 ? (
        <p className="mt-2 text-sm text-slate-600" data-testid="owner-number-none">
          None right now.
        </p>
      ) : (
        <div className="mt-3" data-testid="owner-number-rows">
          <ResultTable result={item.result} limit={PANEL_ROWS} />
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <FromLine freshness={item.freshness} zone={zone} />
        {!readOnly && item.count > Math.min(item.result.rows.length, PANEL_ROWS) && (
          <Link
            to="/reports/$key"
            params={{ key: item.report }}
            className="text-xs font-medium text-brand-strong hover:underline"
          >
            See all {item.count}
            {item.more ? '+' : ''}
          </Link>
        )}
      </div>
      <StaleNote freshness={item.freshness} zone={zone} />
    </>
  );
}

/** A blank figure's value for the earlier period its view names, such as last month's. */
function EarlierValue({ figure, testId }: { figure: Figure; testId: string }) {
  if (!figure.earlier) return null;
  return (
    <p className="mt-1 text-sm text-slate-600" data-testid={testId}>
      {figure.earlier.label}:{' '}
      <span className="font-semibold text-slate-900 tabular-nums">
        {figureText(figure.earlier)}
      </span>
      {figure.unit && <span className="ml-1 text-slate-500">{figure.unit}</span>}
    </p>
  );
}

function FigureCard({
  view,
  figure,
  zone,
  pin,
}: {
  view: string;
  figure: Figure;
  zone: string;
  /** Where the figure is, for someone who may pin it as the owner's number. */
  pin: { view: string; key: string } | null;
}) {
  const pinning = usePin();
  // Nothing to add up yet, such as a month's margin on its first day, says so rather than
  // showing a bare dash, with the earlier period's value when the view names one.
  const blank = figure.value === null;
  const [open, setOpen] = useState(false);
  const opens = useContext(FigureLoaderContext) !== null && !blank;
  return (
    <section
      data-testid={`figure-${figure.key}`}
      className="rounded-xl border border-line bg-surface px-4 py-3.5"
    >
      <h2 className="text-[13px] text-ink-3">{figure.label}</h2>
      {blank && figure.empty ? (
        <p className="mt-1 text-base font-medium text-ink-2">{figure.empty}</p>
      ) : (
        <p
          className={cn(
            'mt-1 text-[26px] leading-tight font-bold tracking-tight text-ink tabular-nums',
            isNegative(figureText(figure)) && 'text-loss',
          )}
        >
          {figureText(figure)}
          {figure.unit && !blank && (
            <span className="ml-1 text-sm font-normal text-ink-3">{figure.unit}</span>
          )}
        </p>
      )}
      {blank && <EarlierValue figure={figure} testId={`figure-${figure.key}-earlier`} />}
      {figure.help && <p className="mt-1 text-xs text-ink-3">{figure.help}</p>}
      <StaleNote freshness={figure.freshness} zone={zone} />
      {opens && <WhatsInIt onClick={() => setOpen(true)} testId={`figure-${figure.key}-open`} />}
      {open && (
        <FigureRecordsModal
          view={view}
          figure={figure.key}
          label={figure.label}
          zone={zone}
          onClose={() => setOpen(false)}
        />
      )}
      {pin && (
        <button
          type="button"
          className="mt-2 mr-3 inline-flex items-center gap-1 text-xs font-medium text-brand-strong hover:underline disabled:opacity-60"
          onClick={() => pinning.mutate(pin)}
          disabled={pinning.isPending}
        >
          <Pin className="size-3" aria-hidden />
          Make this your number
        </button>
      )}
      <AskForChange kind="figure" cardKey={figure.key} label={figure.label} className="mt-2" />
      {pinning.error && <p className="mt-1 text-xs text-red-700">{errorMessage(pinning.error)}</p>}
    </section>
  );
}

/** Opens what makes up a figure. */
function WhatsInIt({ onClick, testId }: { onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      data-testid={testId}
      className="mt-2 mr-3 inline-flex items-center gap-1 text-xs font-medium text-brand-strong hover:underline"
    >
      <ListTree className="size-3" aria-hidden />
      What’s in this number
    </button>
  );
}

/**
 * What makes up a figure: how it is worked out, in words, where its numbers come from and as of
 * when, and the records behind it, each opening its record where there is one to open.
 */
function FigureRecordsModal({
  view,
  figure,
  label,
  zone,
  onClose,
}: {
  view: string;
  figure: string;
  label: string;
  zone: string;
  onClose: () => void;
}) {
  const loader = useContext(FigureLoaderContext);
  const records = useQuery({
    queryKey: loader ? loader.key(view, figure) : ['figure', view, figure],
    queryFn: () => loader!.load(view, figure),
    enabled: loader !== null,
    staleTime: 60_000,
  });
  const data = records.data;
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={label}
      wide
      description={data && <AnswerText parts={data.how} zone={zone} exact />}
    >
      {records.isLoading ? (
        <Spinner />
      ) : records.error || !data ? (
        <ErrorBanner title="Could not show what’s in this number">
          {errorMessage(records.error)}
        </ErrorBanner>
      ) : (
        <div className="space-y-3" data-testid="figure-records">
          <p className="text-[26px] leading-tight font-bold tracking-tight text-ink tabular-nums">
            {figureText(data)}
            {data.unit && data.value !== null && (
              <span className="ml-1 text-sm font-normal text-ink-3">{data.unit}</span>
            )}
          </p>
          <FromLine freshness={data.freshness} zone={zone} />
          <StaleNote freshness={data.freshness} zone={zone} />
          {data.count === 0 ? (
            <p className="text-sm text-ink-2">Nothing is in this number right now.</p>
          ) : (
            <>
              <ResultTable result={data.result} />
              {data.count > data.result.rows.length && (
                <p className="text-xs text-ink-3" data-testid="figure-records-shown">
                  {shownText(data)}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Which of a figure's records the list shows, when it can't show them all. */
function shownText(data: FigureRecords): string {
  const shown = formatNumber(data.result.rows.length);
  const of = `of ${formatNumber(data.count)}`;
  if (data.ranked === 'largest') return `Showing the ${shown} largest ${of}.`;
  if (data.ranked === 'smallest') return `Showing the ${shown} smallest ${of}.`;
  return `Showing ${shown} ${of}.`;
}

/**
 * Words with values in them, the values written as the screen writes numbers and dates: as
 * headline numbers are, rounded, or `exact`, as a condition's threshold must be to say truly
 * which records it takes.
 */
function AnswerText({
  parts,
  zone,
  exact = false,
}: {
  parts: Question['answer'];
  zone: string;
  exact?: boolean;
}) {
  return (
    <>
      {parts.map((part, i) =>
        'text' in part ? (
          <span key={i}>{part.text}</span>
        ) : (
          <strong key={i} className="font-semibold text-ink tabular-nums">
            {exact ? exactValue(part, zone) : answerValue(part, zone)}
          </strong>
        ),
      )}
    </>
  );
}

/** A value written in full, never rounded: every decimal it has, and cents for money at least. */
function exactValue(part: Extract<Question['answer'][number], { value: unknown }>, zone: string) {
  if (!NUMERIC.has(part.type)) return answerValue(part, zone);
  // Decimals arrive as strings and stay strings: a JavaScript number would lose digits.
  const raw = String(part.value);
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!match) return raw;
  const [, sign = '', whole = '0', fraction = ''] = match;
  const decimals = part.type === 'money' ? fraction.padEnd(2, '0') : fraction;
  const text = `${sign}${BigInt(whole).toLocaleString(LOCALE)}${decimals ? `.${decimals}` : ''}`;
  return part.type === 'percent' ? `${text}%` : text;
}

/** Said beside any number a late or failing source is behind, so it is never read as current. */
function StaleNote({ freshness, zone }: { freshness: Freshness; zone: string }) {
  const text = behindText(freshness, zone);
  return text && <p className="mt-2 text-xs text-amber-800">{text}</p>;
}

/** Which sources are behind, and as of when the data is; null while it is current. */
function behindText(freshness: Freshness, zone: string): string | null {
  if (!freshness.stale) return null;
  const behind = freshness.sources.filter((s) => s.state === 'late' || s.state === 'failing');
  const asOf = freshness.as_of ? `, so this is as of ${formatDateTime(freshness.as_of, zone)}` : '';
  return `${listOf(behind.map((s) => s.label))} ${behind.length === 1 ? 'is' : 'are'} behind${asOf}.`;
}

export function listOf(items: string[]): string {
  return new Intl.ListFormat(LOCALE, { style: 'long', type: 'conjunction' }).format(items);
}

function FromLine({ freshness, zone }: { freshness: Freshness; zone: string }) {
  if (freshness.sources.length === 0) return null;
  return (
    <p className="text-xs text-slate-500">
      From {listOf(freshness.sources.map((s) => s.label))}
      {freshness.as_of && `, as of ${formatDateTime(freshness.as_of, zone)}`}.
    </p>
  );
}

function NeedsYou({
  attention,
  clear,
  zone,
}: {
  attention: Attention[];
  clear: View['clear'];
  zone: string;
}) {
  return (
    <Card look="dashboard" title="Needs you today" padded={false} data-tour="needs">
      {attention.length === 0 && (
        <p className="px-4 py-3 text-sm text-ink-2">Nothing needs you here right now.</p>
      )}
      <ul className="divide-y divide-line px-1">
        {attention.map((item) => (
          <AttentionRow key={item.key} item={item} zone={zone} />
        ))}
      </ul>
      {clear.length > 0 && attention.length > 0 && (
        <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3">
          All clear: {listOf(clear.map((c) => c.label.toLowerCase()))}.
        </p>
      )}
    </Card>
  );
}

/** A value in an answer, written as a headline number is, or as a date. */
function answerValue(part: Extract<Question['answer'][number], { value: unknown }>, zone: string) {
  if (part.type === 'date') return formatDate(part.value);
  if (part.type === 'datetime') return formatDateTime(part.value, zone);
  return figureText(part);
}

/** Questions an owner might ask, each answered from the view's numbers; the first is open. */
function Questions({ questions, zone }: { questions: Question[]; zone: string }) {
  const [open, setOpen] = useState<string | null>(questions[0]?.key ?? null);
  return (
    <Card look="dashboard" title="Questions, answered" padded={false} data-tour="questions">
      <ul className="divide-y divide-line px-1" data-testid="questions">
        {questions.map((question) => {
          const shown = open === question.key;
          const panel = `question-${question.key}-answer`;
          return (
            <li key={question.key} data-testid={`question-${question.key}`}>
              <button
                type="button"
                onClick={() => setOpen(shown ? null : question.key)}
                aria-expanded={shown}
                aria-controls={panel}
                className="flex w-full items-start justify-between gap-3 rounded-lg px-3 py-3 text-left text-[15px] text-ink hover:bg-wash"
              >
                {question.ask}
                <ChevronDown
                  className={cn(
                    'mt-0.5 size-4 shrink-0 text-slate-400 transition-transform',
                    shown && 'rotate-180',
                  )}
                  aria-hidden
                />
              </button>
              {shown && (
                <div id={panel} className="px-3 pb-3">
                  <p className="text-sm text-ink-2">
                    <AnswerText parts={question.answer} zone={zone} />
                  </p>
                  <StaleNote freshness={question.freshness} zone={zone} />
                  <AskForChange
                    kind="question"
                    cardKey={question.key}
                    label={question.ask}
                    className="mt-2"
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function amountText(item: Attention): string {
  if (item.amount === null) return '';
  if (item.amount_type === 'money') return formatMoney(item.amount, item.currency);
  if (item.amount_type === 'percent') return `${formatNumber(item.amount)}%`;
  return formatNumber(item.amount);
}

function AttentionRow({ item, zone }: { item: Attention; zone: string }) {
  const [open, setOpen] = useState(false);
  const readOnly = useReadOnlyView();
  const panel = `attention-${item.key}-records`;
  const amount = amountText(item);
  const amountClass = cn(
    'font-mono text-sm text-ink-3 tabular-nums',
    isNegative(amount) && 'text-loss',
  );
  return (
    <li data-testid={`attention-${item.key}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panel}
        className="flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left hover:bg-wash"
      >
        <span
          className={cn('mt-[7px] size-2 shrink-0 rounded-full', SEVERITY_DOT[item.severity])}
          title={item.severity_label}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="sr-only">{item.severity_label}: </span>
          <span className="text-[15px] text-ink">{item.label}</span>{' '}
          <span className="text-sm text-ink-3 tabular-nums">
            {item.count}
            {item.more ? '+' : ''}
          </span>
          {item.help && <span className="mt-0.5 block text-sm text-ink-3">{item.help}</span>}
          <StaleNote freshness={item.freshness} zone={zone} />
          {/* On a phone the amount goes under the label, so the label keeps the width. */}
          <span className={cn(amountClass, 'mt-1 block sm:hidden')}>{amount}</span>
        </span>
        <span className={cn(amountClass, 'hidden pt-0.5 sm:block')}>{amount}</span>
        <ChevronDown
          className={cn(
            'mt-1 size-4 shrink-0 text-slate-400 transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div id={panel} className="space-y-2 px-3 pb-4">
          <ResultTable result={item.result} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FromLine freshness={item.freshness} zone={zone} />
            <AskForChange kind="attention" cardKey={item.key} label={item.label} />
            {!readOnly && item.count > item.result.rows.length && (
              <Link
                to="/reports/$key"
                params={{ key: item.report }}
                className="text-xs font-medium text-brand-strong hover:underline"
              >
                See all {item.count}
                {item.more ? '+' : ''}
              </Link>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/** A report's rows, the first column linking to its record when the row is one. */
function ResultTable({ result, limit }: { result: ReportResult; limit?: number }) {
  const rows = limit === undefined ? result.rows : result.rows.slice(0, limit);
  return (
    <Table stack>
      <thead>
        <tr>
          {result.columns.map((c) => (
            <TH key={c.key} align={NUMERIC.has(c.type) ? 'right' : 'left'}>
              {c.label}
            </TH>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={typeof row['$id'] === 'string' ? row['$id'] : i} className="bg-surface">
            {result.columns.map((c, j) => (
              <TD
                key={c.key}
                align={NUMERIC.has(c.type) ? 'right' : 'left'}
                label={c.label}
                primary={j === 0}
              >
                {j === 0 && typeof row['$id'] === 'string' && !c.reference ? (
                  <RecordLink entity={result.entity} id={row['$id']}>
                    {cellText(c, row, result.titles) || 'Open'}
                  </RecordLink>
                ) : (
                  <ReportCell column={c} row={row} titles={result.titles} />
                )}
              </TD>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function PanelCard({ panel, zone }: { panel: Panel; zone: string }) {
  const { result } = panel;
  const chart = panel.show === 'chart' ? result.chart : null;
  const readOnly = useReadOnlyView();
  return (
    <Card
      look="dashboard"
      title={panel.label}
      padded={false}
      actions={
        readOnly ? undefined : (
          <span className="flex flex-wrap items-center gap-3">
            <AskForChange kind="panel" cardKey={panel.key} label={panel.label} />
            <Link
              to="/reports/$key"
              params={{ key: panel.report }}
              className="text-xs font-medium text-brand-strong hover:underline"
            >
              Open report
            </Link>
          </span>
        )
      }
    >
      <div data-testid={`panel-${panel.key}`} className="space-y-2 p-4">
        {result.rows.length === 0 ? (
          <EmptyState title="Nothing here yet" />
        ) : chart && (chart.type === 'line' || chart.type === 'bar') ? (
          <TimeChart result={result} x={chart.x} y={chart.y} kind={chart.type} />
        ) : chart ? (
          <BarChart result={result} x={chart.x} y={chart.y} />
        ) : (
          <ResultTable result={result} limit={PANEL_ROWS} />
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <FromLine freshness={panel.freshness} zone={zone} />
          {!chart && result.rows.length > PANEL_ROWS && (
            <span className="text-xs text-slate-500">
              Showing {PANEL_ROWS} of {result.rows.length}
              {result.truncated ? '+' : ''}
            </span>
          )}
        </div>
        <StaleNote freshness={panel.freshness} zone={zone} />
      </div>
    </Card>
  );
}

const monthLabel = new Intl.DateTimeFormat(LOCALE, { month: 'short', timeZone: 'UTC' });
const dayLabel = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** What a bucket reads as under a chart: Jul 6 for a week, Oct for a month. */
function bucketLabel(column: ReportColumn, value: unknown): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value))
    return value === null || value === undefined ? '' : String(value);
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (column.bucket === 'month') return monthLabel.format(date);
  if (column.bucket === 'quarter')
    return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
  if (column.bucket === 'year') return String(date.getUTCFullYear());
  if (column.bucket === 'week' || column.bucket === 'day') return dayLabel.format(date);
  return formatDate(value);
}

/**
 * A chart over time, drawn as SVG: a line for amounts that come and go week to week, bars
 * for monthly totals. The last bucket is still filling up, so it is marked "so far" and
 * drawn paler rather than read as a drop.
 */
function TimeChart({
  result,
  x,
  y,
  kind,
}: {
  result: ReportResult;
  x: string;
  y: string;
  kind: 'line' | 'bar';
}) {
  const xColumn = result.columns.find((c) => c.key === x);
  const yColumn = result.columns.find((c) => c.key === y);
  if (!xColumn || !yColumn) return null;
  const currency =
    yColumn.type === 'money'
      ? (yColumn.currency ??
        (typeof result.rows[0]?.['$currency'] === 'string' ? result.rows[0]['$currency'] : null))
      : null;
  const points = result.rows.map((row) => ({
    label: bucketLabel(xColumn, row[x]),
    value: Number(row[y] ?? 0),
    text: cellText(yColumn, row, result.titles),
  }));
  const partial = xColumn.bucket !== null;
  const max = Math.max(...points.map((p) => p.value), 0);
  const scale = (v: number) => (max > 0 ? v / max : 0);
  const width = 520;
  const height = 200;
  const top = 18;
  const plot = height - top;
  const n = points.length;
  const step = width / Math.max(n, 1);
  const every = n > 8 ? 2 : 1;
  const maxText =
    yColumn.type === 'money' ? formatCompactMoney(max, currency) : formatNumber(max.toFixed(1));

  return (
    <figure>
      <svg
        viewBox={`0 0 ${width} ${height + 34}`}
        className="w-full"
        role="img"
        aria-label={`${yColumn.label} by ${xColumn.label.toLowerCase()}`}
      >
        <text x={width} y={10} textAnchor="end" className="fill-slate-500 text-[11px]">
          {maxText}
        </text>
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={0}
            x2={width}
            y1={top + plot * (1 - f)}
            y2={top + plot * (1 - f)}
            className="stroke-slate-200"
            strokeDasharray={f === 0 ? undefined : '3 3'}
          />
        ))}
        {kind === 'bar'
          ? points.map((p, i) => {
              const h = plot * scale(p.value);
              const last = partial && i === n - 1;
              return (
                <rect
                  key={i}
                  x={i * step + step * 0.08}
                  y={top + plot - h}
                  width={step * 0.84}
                  height={h}
                  className={last ? 'fill-brand-muted' : 'fill-brand'}
                >
                  <title>{`${p.label}${last ? ' (so far)' : ''}: ${p.text}`}</title>
                </rect>
              );
            })
          : (() => {
              const at = (i: number) => i * step + step / 2;
              const yAt = (v: number) => top + plot * (1 - scale(v));
              const settled = partial ? points.slice(0, -1) : points;
              const path = settled
                .map((p, i) => `${i === 0 ? 'M' : 'L'}${at(i)},${yAt(p.value)}`)
                .join(' ');
              const lastIndex = n - 1;
              const last = points[lastIndex];
              const beforeLast = points[lastIndex - 1];
              return (
                <>
                  <path d={path} fill="none" className="stroke-brand" strokeWidth={2} />
                  {partial && last && beforeLast && (
                    <line
                      x1={at(lastIndex - 1)}
                      y1={yAt(beforeLast.value)}
                      x2={at(lastIndex)}
                      y2={yAt(last.value)}
                      className="stroke-brand-muted"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                    />
                  )}
                  {points.map((p, i) => (
                    <circle
                      key={i}
                      cx={at(i)}
                      cy={yAt(p.value)}
                      r={3.5}
                      className={partial && i === lastIndex ? 'fill-brand-muted' : 'fill-brand'}
                    >
                      <title>{`${p.label}${partial && i === lastIndex ? ' (so far)' : ''}: ${p.text}`}</title>
                    </circle>
                  ))}
                </>
              );
            })()}
        {points.map((p, i) =>
          i % every === 0 || i === n - 1 ? (
            <g key={i}>
              <text
                x={i * step + step / 2}
                y={height + 14}
                textAnchor="middle"
                className="fill-slate-500 text-[11px]"
              >
                {p.label}
              </text>
              {partial && i === n - 1 && (
                <text
                  x={i * step + step / 2}
                  y={height + 28}
                  textAnchor="middle"
                  className="fill-slate-400 text-[10px]"
                >
                  so far
                </text>
              )}
            </g>
          ) : null,
        )}
      </svg>
      <figcaption className="sr-only">
        {points.map((p) => `${p.label}: ${p.text}`).join('; ')}
      </figcaption>
    </figure>
  );
}

/** "Sample data", as the tag on databink.com's sample screen reads. */
export function SamplePill({ title }: { title?: string }) {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full bg-sample px-2.5 py-0.5 font-mono text-xs text-sample-ink"
      title={title}
      data-testid="sample-pill"
    >
      Sample data
      {/* What the hover says, for a screen reader, and for the tests. */}
      {title && <span className="sr-only">. {title}</span>}
    </span>
  );
}

/** The badge in the header on every screen while the sample is loaded. */
export function SampleBadge(): ReactNode {
  const meta = useAppMeta();
  if (!meta.dashboard.sample) return null;
  return (
    <span
      data-testid="sample-badge"
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium whitespace-nowrap text-amber-900"
    >
      <FlaskConical className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">
        Sample data<span className="hidden sm:inline">: invented for a demonstration</span>
      </span>
    </span>
  );
}
